/**
 * 프론트엔드 오류 수집 · 사용자 오류 신고 (웹 전용)
 *
 * 왜 필요한가: 백엔드 오류는 서버 로그에 남지만, 브라우저에서 난 오류는 사용자 PC 에서 사라진다.
 * 설치형은 로그 파일 + 「도움말 › 로그 보기」로 해결했고(ChunkErrorBoundary · LogViewerDialog),
 * 웹은 QBase 가 제공하는 RUM 수집 경로로 QPM 에 남긴다.
 *
 *   브라우저 ──(세션 쿠키)──▶ QBase /qbase/api/observability/client-metrics ──(토큰)──▶ QPM
 *
 * 브라우저가 QPM(:3006)을 직접 부르지 않는 이유는 QBase QpmClient#postClientMetrics 참조
 * (인증서 호스트명 · CORS · 수집 토큰 노출). utils/rum.js 의 직접 전송 경로를 쓰지 않는 것도 같은 이유다.
 * rum.js(QPM_ENABLED) 를 다시 켜면 fetch/XHR 가 두 SDK 에 이중으로 감싸진다 — 둘 중 하나만 쓸 것.
 *
 * 세 갈래:
 *  1. 자동 수집 — QBase RUM SDK 가 전역 에러·unhandledrejection·API 호출·클릭·화면 전환을 보낸다.
 *     React 가 잡은 렌더 에러는 window.onerror 로 올라가지 않으므로 ErrorBoundary 가 reportCaughtError 로 넘긴다.
 *  2. 사용 기록 — 메뉴 선택(menu_select)과 기능 결과(feature_use: 열기·저장·내보내기·DDL).
 *     클릭만으로는 "무엇을 했는지" 가 남지 않아(캔버스·단축키·결과 성공 여부) 앱이 직접 남긴다.
 *  3. 사용자 신고 — 「도움말 › 오류 신고」. 최근 오류와 환경 정보를 묶어 custom_event(error_report) 로 보낸다.
 *     SDK 는 전송 결과를 알려 주지 않으므로 신고만은 직접 보내고 **서버 확인을 받아야 성공으로 표시**한다.
 *
 * SDK 는 QPM 이 소유하고(window.__qpmRum), QBase 는 SDK·수집을 그대로 전달만 한다.
 * 신원은 싣지 않는다 — QBase 수집 프록시가 세션에서 단언하고 QPM 이 identity 로 기록한다.
 * 무엇을 받을지(로그인 전 허용 범위·클릭 이름 방식·가릴 URL 키)는 QPM 앱별 수집 정책(QSY0000005)이 정한다 —
 * 이 파일이나 QBase 를 고치지 않는다.
 *
 * 조회(개발자·AI): QPM GET /api/client-metrics/errors?appName=QSY0000005
 *                        GET /api/client-metrics/events?appName=QSY0000005&name=error_report
 *                        GET /api/client-metrics/events?appName=QSY0000005&name=feature_use
 */

import EventBus from './hooks/EventBus';

/** QBase 에 등록된 데이터모델러 시스템 ID — QPM 에서 appName 으로 묶인다 */
export const SYSTEM_ID = 'QSY0000005';
export const REPORT_EVENT_NAME = 'error_report';
export const FEATURE_EVENT_NAME = 'feature_use';
export const MENU_EVENT_NAME = 'menu_select';
export const DESCRIPTION_MAX = 2000;
/** QBase 중계 상한(256KB)보다 넉넉히 아래. 넘으면 오래된 오류부터 뺀다 */
export const REPORT_MAX_BYTES = 64 * 1024;

const OBS_PATH = '/qbase/api/observability';
const RECENT_MAX = 20;
const REPORT_ERRORS_MAX = 10;
const PENDING_MAX = 20;
const STACK_MAX = 2000;
const MESSAGE_MAX = 500;
/** URL 쿼리에서 가릴 이름. whereconds 는 SQL 조건이 그대로 실려 QPM 에 남는 것이 확인됐다 */
const SENSITIVE_PARAMS = ['token', 'key', 'password', 'secret', 'auth', 'apikey', 'api_key', 'access_token', 'whereconds'];

let recentErrors = [];
let pendingForward = [];
let pendingEvents = [];
let initialized = false;

const truncate = (value, max) => {
  const s = value == null ? '' : String(value);
  return s.length > max ? `${s.slice(0, max)}…(+${s.length - max}자)` : s;
};

/** Error 가 아닌 reject 사유(문자열·응답 객체 등)도 읽을 수 있는 글로 만든다 */
const toText = (value) => {
  if (typeof value === 'string') return value;
  try {
    const json = JSON.stringify(value);
    if (json !== undefined) return json;
  } catch (_) { /* 순환 참조 등 */ }
  return String(value);
};

const utf8Length = (str) => {
  let n = 0;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff) { n += 4; i++; }
    else n += 3;
  }
  return n;
};

/** 경로+쿼리만 남기고 민감한 쿼리 값을 가린다 */
export function redactUrl(url) {
  if (!url) return '';
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const u = new URL(String(url), base);
    [...u.searchParams.keys()].forEach((k) => {
      if (SENSITIVE_PARAMS.includes(k.toLowerCase())) u.searchParams.set(k, 'REDACTED');
    });
    return `${u.pathname}${u.search}`;
  } catch (_) {
    return '';
  }
}

export function normalizeError(error, context = {}) {
  const isObject = error !== null && typeof error === 'object';
  const isErrorLike = isObject && 'message' in error;
  return {
    at: new Date().toISOString(),
    type: context.type || 'error',
    name: truncate(isErrorLike && error.name ? error.name : 'Error', 100),
    message: truncate(isErrorLike ? error.message : toText(error), MESSAGE_MAX),
    stack: isErrorLike && error.stack ? truncate(error.stack, STACK_MAX) : undefined,
    componentStack: context.componentStack ? truncate(context.componentStack, STACK_MAX) : undefined,
    url: redactUrl(typeof window !== 'undefined' ? window.location.href : ''),
  };
}

/** 신고에 담을 최근 오류를 기억한다 (전송하지 않음 — 자동 전송은 SDK 담당) */
export function recordError(error, context = {}) {
  const entry = normalizeError(error, context);
  recentErrors.push(entry);
  if (recentErrors.length > RECENT_MAX) recentErrors = recentErrors.slice(-RECENT_MAX);
  return entry;
}

export const getRecentErrors = () => recentErrors.slice();

const forwardToSdk = (error, context) => {
  const rum = typeof window !== 'undefined' ? window.__qpmRum : null;
  if (rum && typeof rum.trackError === 'function') {
    rum.trackError(error, context);
    return true;
  }
  return false;
};

const sendEvent = (name, data) => {
  const rum = typeof window !== 'undefined' ? window.__qpmRum : null;
  if (rum && typeof rum.trackEvent === 'function') {
    rum.trackEvent(name, data);
    return true;
  }
  return false;
};

/** SDK 로드 전이면 잠시 쌓아 둔다 (첫 화면의 메뉴 선택을 잃지 않게) */
const sendOrQueueEvent = (name, data) => {
  if (sendEvent(name, data)) return;
  pendingEvents.push([name, data]);
  if (pendingEvents.length > PENDING_MAX) pendingEvents = pendingEvents.slice(-PENDING_MAX);
};

const flushPending = () => {
  const queued = pendingForward;
  pendingForward = [];
  queued.forEach(([error, context]) => forwardToSdk(error, context));
  const events = pendingEvents;
  pendingEvents = [];
  events.forEach(([name, data]) => sendEvent(name, data));
};

/**
 * 기능 사용 기록 — custom_event(feature_use).
 * 결과가 나는 지점(저장 성공·내보내기 실행)에서 부른다. 메뉴를 연 것만으로는 쓰지 않는다 — 그건 menu_select 가 남긴다.
 * 설치형·초기화 전에는 아무것도 하지 않는다.
 *
 * @param {string} feature model_open · model_save · model_export · ddl_export
 * @param {object} data    건수·형식 같은 분류값만. **모델명 등 사용자가 만든 글은 싣지 않는다**
 */
export function trackFeature(feature, data = {}) {
  if (!initialized) return;
  sendOrQueueEvent(FEATURE_EVENT_NAME, { ...data, feature });
}

/**
 * 앱이 직접 잡은 오류(React ErrorBoundary 등)를 기록하고 QPM 으로 보낸다.
 * 전역 핸들러로는 보이지 않는 오류만 여기로 넘길 것 — 전역 오류는 SDK 가 이미 보낸다.
 */
export function reportCaughtError(error, context = {}) {
  const entry = recordError(error, context);
  const sdkError = error instanceof Error ? error : new Error(entry.message);
  const sdkContext = { type: entry.type, componentStack: entry.componentStack };
  if (!forwardToSdk(sdkError, sdkContext) && initialized) {
    // SDK 스크립트가 아직 로드 중 — 초기 렌더에서 난 오류를 잃지 않도록 잠시 쌓아 둔다
    pendingForward.push([sdkError, sdkContext]);
    if (pendingForward.length > PENDING_MAX) pendingForward = pendingForward.slice(-PENDING_MAX);
  }
  return entry;
}

/**
 * 웹에서만 QBase RUM SDK 를 붙이고 최근 오류 기록을 시작한다.
 * @returns {boolean} 초기화했으면 true (설치형·중복 호출은 false)
 */
export function initFrontendMonitoring({ beApi, isStandalone, context }) {
  if (initialized || isStandalone || typeof window === 'undefined') return false;
  initialized = true;

  // 자원 로드 실패(img/script)는 message 도 error 도 없다 — 코드 오류가 아니므로 기록하지 않는다
  window.addEventListener('error', (e) => {
    if (e && (e.error || e.message)) recordError(e.error || e.message, { type: 'error' });
  });
  window.addEventListener('unhandledrejection', (e) => {
    recordError(e ? e.reason : e, { type: 'unhandledrejection' });
  });

  // 메뉴 사용 — 헤더 메뉴·서브메뉴·즐겨찾기·코드에서 여는 메뉴가 모두 toolBarSelect 한 이벤트로 모인다
  EventBus.on('toolBarSelect', (shortLabel) => sendOrQueueEvent(MENU_EVENT_NAME, { menu: shortLabel }));

  // 모든 기록에 실을 태그(배포 형태·에디션 등) — QPM SDK 일반 규약. SDK 가 이미 떠 있으면 setContext,
  // 아니면 로드될 때 window.__qpmRumContext 를 읽는다. 테스트 러너 등이 먼저 넣은 태그는 유지한다.
  if (context) {
    if (window.__qpmRum && typeof window.__qpmRum.setContext === 'function') {
      window.__qpmRum.setContext(context);
    } else {
      window.__qpmRumContext = { ...(window.__qpmRumContext || {}), ...context };
    }
  }

  if (window.__qpmRum) {
    flushPending();
    return true;
  }
  const script = document.createElement('script');
  // 클릭 이름 방식·가릴 URL 키 등은 QPM 앱별 정책이 정한다. 기본이 '선언 이름만'이라 모델·엔터티 목록의 글자
  // (사용자가 만든 이름)는 남지 않는다. 이름이 필요한 곳은 data-qbase-action 으로 선언한다.
  script.src = `${beApi}${OBS_PATH}/rum.js?sys=${SYSTEM_ID}`;
  script.defer = true;
  script.onload = flushPending;
  script.onerror = () => {
    pendingForward = [];
    pendingEvents = [];
    // 조용히 넘기지 않는다 — 자동 수집이 꺼진 상태인 줄 모르면 "오류가 없다" 로 오해한다
    console.warn('[frontendMonitoring] QBase RUM SDK 를 불러오지 못했습니다 — 자동 오류 수집이 동작하지 않습니다.');
  };
  document.head.appendChild(script);
  return true;
}

/** 접수번호 — 사용자가 문의할 때 부르기 쉬운 형태 (ER-20260914-153012-A1F3) */
export function createReportId(date = new Date(), random = Math.random) {
  const p = (n) => String(n).padStart(2, '0');
  const day = `${date.getFullYear()}${p(date.getMonth() + 1)}${p(date.getDate())}`;
  const time = `${p(date.getHours())}${p(date.getMinutes())}${p(date.getSeconds())}`;
  const tail = Math.floor(random() * 0x10000).toString(16).toUpperCase().padStart(4, '0');
  return `ER-${day}-${time}-${tail}`;
}

export function collectEnv(extra = {}) {
  const w = typeof window !== 'undefined' ? window : null;
  const n = typeof navigator !== 'undefined' ? navigator : null;
  let timeZone = '';
  try { timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (_) { /* 미지원 */ }
  return {
    appVersion: process.env.REACT_APP_VERSION || '',
    buildTime: process.env.BUILD_TIME || '',
    pageUrl: redactUrl(w ? w.location.href : ''),
    userAgent: n ? n.userAgent : '',
    language: n ? n.language : '',
    viewport: w ? `${w.innerWidth}x${w.innerHeight}` : '',
    timeZone,
    reportedAt: new Date().toISOString(),
    ...extra,
  };
}

/** QBase 중계가 받는 RUM 배치 형식으로 신고 한 건을 만든다 */
export function buildErrorReport({ description, errors = [], env = {}, reportId, rumSessionId = '', now = Date.now() }) {
  const make = (list) => JSON.stringify({
    metrics: [{
      type: 'custom_event',
      timestamp: now,
      sessionId: reportId,
      appName: SYSTEM_ID,
      url: env.pageUrl || '',
      userAgent: env.userAgent || '',
      data: {
        name: REPORT_EVENT_NAME,
        reportId,
        // 같은 페이지 로드에서 SDK 가 보낸 api_call·error·user_action 의 sessionId — 신고와 자동 수집을 잇는 키
        rumSessionId,
        description: truncate(description, DESCRIPTION_MAX),
        // 사용자 ID 는 싣지 않는다 — 수집 프록시가 세션에서 단언한 값만 QPM 이 identity.userId 로 남긴다
        recentErrorCount: errors.length,
        errors: list,
        env,
      },
    }],
    meta: { appName: SYSTEM_ID, sessionId: reportId, timestamp: now },
  });

  let list = errors.slice(-REPORT_ERRORS_MAX);
  let body = make(list);
  while (utf8Length(body) > REPORT_MAX_BYTES && list.length > 0) {
    list = list.slice(1);
    body = make(list);
  }
  if (utf8Length(body) > REPORT_MAX_BYTES) {
    throw new Error('신고 내용이 너무 커서 보낼 수 없습니다. 설명을 줄여 주세요.');
  }
  return { body, includedErrors: list.length };
}

/**
 * 사용자 오류 신고를 보낸다. 서버가 저장을 확인했을 때만 성공을 돌려준다.
 * @returns {Promise<{reportId: string, includedErrors: number}>}
 */
export async function sendErrorReport({ beApi, description, envExtra, fetchImpl }) {
  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
  if (!doFetch) throw new Error('이 브라우저에서는 신고를 보낼 수 없습니다.');

  const reportId = createReportId();
  const rum = typeof window !== 'undefined' ? window.__qpmRum : null;
  const { body, includedErrors } = buildErrorReport({
    description, errors: getRecentErrors(), env: collectEnv(envExtra), reportId,
    rumSessionId: (rum && rum.sessionId) || '',
  });

  let res;
  try {
    res = await doFetch(`${beApi}${OBS_PATH}/client-metrics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      credentials: 'include',
    });
  } catch (_) {
    throw new Error('네트워크 오류로 신고를 보내지 못했습니다. 잠시 후 다시 시도해 주세요.');
  }

  let json = null;
  try { json = await res.json(); } catch (_) { /* 본문 없음 */ }

  // 응답은 QPM 수집 API 그대로다(QBase 는 전달만 한다):
  //   200 { success, processed, rejected, rejectReasons } · 4xx/502 { error, message }
  if (res.status === 401) {
    throw new Error('로그인이 만료되어 신고를 보내지 못했습니다. 다시 로그인한 뒤 시도해 주세요.');
  }
  if (res.status === 403) {
    throw new Error('이 서비스의 오류 수집이 꺼져 있어 신고를 보낼 수 없습니다.');
  }
  if (res.status === 429) {
    throw new Error('요청이 너무 많아 신고를 보내지 못했습니다. 잠시 후 다시 시도해 주세요.');
  }
  if (res.status === 502) {
    throw new Error('오류 수집 서버에 연결할 수 없어 신고를 저장하지 못했습니다.');
  }
  if (!res.ok || !json || json.success !== true) {
    throw new Error(`신고를 접수하지 못했습니다 (${(json && (json.message || json.error)) || `HTTP ${res.status}`}).`);
  }
  if (!(json.processed >= 1)) {
    throw new Error('수집 서버가 신고를 저장하지 않았습니다.');
  }
  return { reportId, includedErrors };
}

/** 테스트 전용 — 모듈 상태 초기화 */
export function _resetForTest() {
  recentErrors = [];
  pendingForward = [];
  pendingEvents = [];
  initialized = false;
}
